/**
 * Changing a password, and resetting somebody else's.
 *
 * Split out of Settings because the two cards carry a fair amount of state between them and
 * Settings is long enough already.
 *
 * The honesty rule this file exists to keep: both endpoints answer with `durable`, and a `false`
 * means no database is configured, so the change lasts only until the control plane restarts and
 * `BASTION_SEED_ACCOUNTS` puts the old password back. That is reported here rather than left for
 * somebody to discover at the next sign-in — the same principle as ADR-0012, applied to an act
 * rather than to a feature.
 */

import { useState, type FormEvent } from 'react'
import { api } from '../api'
import { useSession } from '../App'
import { Async, Card, Chip, Note, useAsync, useToast } from '../components/ui'

/** The minimum the control plane enforces. Stated here so the form can say so before a round trip. */
const MIN_LENGTH = 12

type ChangeResult = { durable: boolean; other_sessions_ended: number; note: string }

export function ChangePasswordCard() {
  const { me } = useSession()
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<ChangeResult | null>(null)

  // Somebody who signed in through the identity provider has no local password, and a form that
  // offered to change one would be offering something this platform does not own.
  if (!me.local) {
    return (
      <Card title="Password">
        <Note>
          You sign in through your organization's identity provider, so your password is managed
          there rather than here.
        </Note>
      </Card>
    )
  }

  // Checked here only to keep the button honest before a round trip; the control plane enforces
  // both of these regardless, and its refusal is what the user actually sees if this is wrong.
  const tooShort = next.length > 0 && next.length < MIN_LENGTH
  const mismatch = confirm.length > 0 && next !== confirm
  const ready = current.length > 0 && next.length >= MIN_LENGTH && next === confirm && !busy

  function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    api
      .post<ChangeResult>('/auth/password', { current_password: current, new_password: next })
      .then((r) => {
        setDone(r)
        setCurrent('')
        setNext('')
        setConfirm('')
        toast('ok', 'Password changed.')
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setBusy(false))
  }

  return (
    <Card title="Password" subtitle="Changing it signs out your other sessions">
      {done ? (
        <>
          <Note tone={done.durable ? 'ok' : 'warn'}>{done.note}</Note>
          <div className="small faint" style={{ marginTop: 8 }}>
            {done.other_sessions_ended === 0
              ? 'You had no other sessions open.'
              : `${done.other_sessions_ended} other session${done.other_sessions_ended === 1 ? '' : 's'} ended.`}
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="sm" onClick={() => setDone(null)}>
              Change it again
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={submit}>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Note tone="danger">{error}</Note>
            </div>
          )}
          <div className="field">
            <label htmlFor="cur">Current password</label>
            <input
              id="cur"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
            {/* The reason is worth stating. Asked for it while already signed in, most people
                assume it is a formality. */}
            <div className="hint">
              Required even though you are signed in. A session proves somebody signed in, not that
              it is still you at the keyboard.
            </div>
          </div>
          <div className="field">
            <label htmlFor="new">New password</label>
            <input
              id="new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <div className="hint">
              At least {MIN_LENGTH} characters. There is no rule about capitals or digits — length
              is what makes a password expensive to guess.
            </div>
          </div>
          <div className="field">
            <label htmlFor="conf">New password again</label>
            <input
              id="conf"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {tooShort && <div className="hint">Too short by {MIN_LENGTH - next.length}.</div>}
            {mismatch && <div className="hint">These do not match.</div>}
          </div>
          <button className="primary" type="submit" disabled={!ready}>
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </form>
      )}
    </Card>
  )
}

type Account = {
  username: string
  display_name: string
  roles: string[]
  durable: boolean
  resettable: boolean
}

type ResetResult = { durable: boolean; sessions_ended: number; note: string }

/** Administrator view: who has a local account here, and setting a password for one of them. */
export function AccountsCard() {
  const toast = useToast()
  const { me } = useSession()
  const [target, setTarget] = useState<Account | null>(null)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [changed, setChanged] = useState(0)
  const accounts = useAsync<Account[]>(() => api.get<Account[]>('/accounts'), [me.tenant, changed])

  function reset(e: FormEvent) {
    e.preventDefault()
    if (!target) return
    setBusy(true)
    setError(null)
    api
      .post<ResetResult>(`/accounts/${encodeURIComponent(target.username)}/password`, {
        new_password: value,
      })
      .then((r) => {
        toast(r.durable ? 'ok' : 'error', r.note)
        setTarget(null)
        setValue('')
        setChanged((n) => n + 1)
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setBusy(false))
  }

  return (
    <Card
      title="Local accounts"
      subtitle="Set a password for somebody who cannot sign in — their sessions all end"
    >
      <Async state={accounts}>
        {(rows) => (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>Roles</th>
                    <th>Password</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.username}>
                      <td>
                        {a.display_name}
                        <div className="faint mono small">{a.username}</div>
                      </td>
                      <td>
                        {a.roles.map((r) => (
                          <Chip key={r} tone="outline">
                            {r.replace(/_/g, ' ')}
                          </Chip>
                        ))}
                      </td>
                      <td>
                        {/* Which accounts would revert on a restart is an administrator's problem,
                            so it is a column rather than a footnote. */}
                        {a.durable ? (
                          <Chip tone="ok">stored</Chip>
                        ) : (
                          <Chip tone="warn">from configuration</Chip>
                        )}
                      </td>
                      <td>
                        {a.resettable ? (
                          <button className="sm" onClick={() => setTarget(a)}>
                            Set password
                          </button>
                        ) : a.username === me.username ? (
                          <span className="faint small">you</span>
                        ) : (
                          /* The server decides this, and says why here rather than offering a
                             button that would be refused after somebody types a password. */
                          <span className="faint small" title="This account holds a capability you do not">
                            not yours to reset
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {target && (
              <form onSubmit={reset} style={{ marginTop: 12 }}>
                {error && (
                  <div style={{ marginBottom: 12 }}>
                    <Note tone="danger">{error}</Note>
                  </div>
                )}
                <div className="field">
                  <label htmlFor="reset-pw">
                    New password for {target.display_name} ({target.username})
                  </label>
                  <input
                    id="reset-pw"
                    type="password"
                    autoComplete="new-password"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                  <div className="hint">
                    At least {MIN_LENGTH} characters. Tell them out of band, and have them change it
                    — you will know this one.
                  </div>
                </div>
                <div className="row">
                  <button className="primary" type="submit" disabled={busy || value.length < MIN_LENGTH}>
                    {busy ? 'Setting…' : 'Set password'}
                  </button>
                  <button
                    type="button"
                    className="sm"
                    onClick={() => {
                      setTarget(null)
                      setValue('')
                      setError(null)
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <Note>
              A reset ends every one of that person's sessions, including any they are using — it is
              what you do when an account may be in somebody else's hands. Changing your own password
              is the card above, which asks for the current one; this route does not, so it does not
              point at you.
            </Note>
            {/* ADR-0012: a gap the operator can see gets compensated for; one they cannot see gets
                trusted. Adding an account is still an environment edit, and this is the screen where
                somebody would go looking for it. */}
            <Note tone="warn">
              This list is everyone with a local account here, and it is not editable from the
              console. Adding or removing someone means editing{' '}
              <span className="mono">BASTION_SEED_ACCOUNTS</span> and restarting the control plane.
              Passwords are the part that can be changed while it runs.
            </Note>
          </>
        )}
      </Async>
    </Card>
  )
}
